const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyA0IKXLQ2Dw2XhH2dMU79yfC-CunC-UBss';

/**
 * What a map component (google-map-react's `bootstrapURLKeys`) should load the
 * Maps API with: this key and the Places library. The Maps API is loaded ONCE
 * per page, by whoever gets there first — and on the normal path that is the
 * shop page's footer map, not the checkout. A map that loads it without Places
 * leaves the checkout's address search with nothing to call (UX-SHOP-4).
 */
export const GOOGLE_MAPS_BOOTSTRAP_KEYS = Object.freeze({ key: GMAPS_KEY, libraries: ['places'] });

// How long we're willing to wait for the script to load/init before giving up.
// Without this, a stalled network request or a request that a host silently
// swallows (e.g. some in-app browsers / restrictive proxies) never calls
// `script.onerror` NOR the success callback, and the caller's spinner spins
// forever — that's the "current location just keeps loading" report.
const LOAD_TIMEOUT_MS = 12000;

let gmapsPromise = null;

// Google calls this global if it loads but the key is rejected (bad key,
// referrer restriction, API not enabled, billing disabled, etc). Crucially,
// this fires *separately* from — and does not block — the normal
// `callback=` bootstrap, so from our promise's point of view the load
// "succeeds" while every real API call afterwards (predictions, geocoding)
// silently fails with REQUEST_DENIED. That reads to a customer as "I type
// but no suggestions ever show up", with zero error surfaced anywhere.
// Track it so callers can tell "loaded but broken" apart from "still fine".
let authFailed = false;
if (typeof window !== 'undefined' && !window.gm_authFailure) {
  window.gm_authFailure = () => {
    authFailed = true;
  };
}

export function isGoogleMapsAuthFailed() {
  return authFailed;
}

function withTimeout(promise, ms) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error('timeout')), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

/**
 * Places from an API that is ALREADY loaded — by a map that did not ask for
 * it. `importLibrary` is how the current Maps API adds a library after the
 * fact; loading a second copy of the API instead is refused by Google.
 */
function importPlacesIntoLoadedMaps() {
  const maps = window.google?.maps;
  if (maps?.places) return Promise.resolve();
  if (typeof maps?.importLibrary !== 'function') {
    return Promise.reject(new Error('places library unavailable'));
  }
  return Promise.resolve(maps.importLibrary('places')).then((lib) => {
    // The legacy namespace is normally filled in by the import; make sure the
    // callers' `google.maps.places.AutocompleteService` resolves either way.
    if (!window.google.maps.places && lib) window.google.maps.places = lib;
    if (!window.google.maps.places) throw new Error('places library unavailable');
  });
}

/** Load Google Maps JS API with Places library (singleton). */
export function loadGoogleMaps() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.google?.maps?.places) return Promise.resolve();
  if (gmapsPromise) return gmapsPromise;

  const reset = (err) => {
    gmapsPromise = null;
    throw err;
  };

  // The API is here, Places is not: a map loaded it first (UX-SHOP-4).
  if (window.google?.maps) {
    gmapsPromise = withTimeout(importPlacesIntoLoadedMaps(), LOAD_TIMEOUT_MS).catch(reset);
    return gmapsPromise;
  }

  const existing = document.querySelector('script[src*="maps.googleapis.com"]');
  if (existing) {
    // Somebody's script is still loading. Wait for the API itself — not for
    // Places, which that script may never bring — then add Places to it.
    gmapsPromise = withTimeout(new Promise((resolve) => {
      const timer = setInterval(() => {
        const maps = window.google?.maps;
        if (maps?.places || typeof maps?.importLibrary === 'function') {
          clearInterval(timer);
          resolve();
        }
      }, 80);
    }).then(importPlacesIntoLoadedMaps), LOAD_TIMEOUT_MS).catch(reset);
    return gmapsPromise;
  }

  gmapsPromise = withTimeout(new Promise((resolve, reject) => {
    const cb = `__gmc_${Date.now()}`;
    window[cb] = () => {
      resolve();
      delete window[cb];
    };
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GMAPS_KEY}&libraries=places&callback=${cb}`;
    script.async = true;
    script.onerror = reject;
    document.head.appendChild(script);
  }), LOAD_TIMEOUT_MS).catch((err) => {
    // Let a later retry try again instead of being stuck with a rejected
    // singleton forever (e.g. a flaky network blip on first load).
    gmapsPromise = null;
    throw err;
  });
  return gmapsPromise;
}

export function parsePlaceComponents(place) {
  const comps = place?.address_components || [];
  const city = (
    comps.find((c) => c.types.includes('locality'))
    || comps.find((c) => c.types.includes('administrative_area_level_2'))
    || comps.find((c) => c.types.includes('administrative_area_level_1'))
  )?.long_name || '';
  const country = comps.find((c) => c.types.includes('country'))?.short_name || '';
  // Street/house number — used to auto-fill the building-number field.
  const buildingNumber = (
    comps.find((c) => c.types.includes('street_number'))
    || comps.find((c) => c.types.includes('premise'))
  )?.long_name || '';
  const address = place?.formatted_address || '';
  const lat = place?.geometry?.location?.lat?.();
  const lng = place?.geometry?.location?.lng?.();
  return {
    address,
    city,
    country,
    buildingNumber,
    lat: typeof lat === 'number' ? lat : null,
    lng: typeof lng === 'number' ? lng : null,
  };
}
